<template>
  <div
    class="compgroup"
    :class="classObject"
    @dragenter="dragEnter = true"
    @dragleave="dragEnter = false"
    @dragover.prevent
    @mousedown="onMousedownCompgroup"
  >
    <vddl-list
      class="panel__body--list"
      :list="components"
      :horizontal="true"
      :drop="onDropped"
      :allowed-types="allowedTypes"
      :disable-if="!canEdit"
    >
      <component-collection-item
        v-for="(component, index) in components"
        :id="id"
        :key="component.id"
        :list="components"
        :item="comps[index]"
        :draggable="component"
        :index="index"
        :wrapper="components"
        :type="type"
        :disable-if="!canEdit"
        :move-action="moveAction"
        :delete-action="deleteAction"
      />
      <vddl-placeholder>
        <div class="droparea border-dance" />
      </vddl-placeholder>
    </vddl-list>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import { useProtect } from '@/composables/useProtect.js';
import { SYS_PERMISSIONS, DRAG_TYPE } from '@/constants.js';
import ComponentCollectionItem from './ComponentCollectionItem.vue';

export default {
  components: {
    ComponentCollectionItem,
  },

  props: {
    id: {
      type: String,
      default: null,
    },
    // The component references that the list contains, format: { id, component }
    components: {
      type: Array,
      default: null,
    },
    // The type of the dragged item for this list e.g. DRAG_TYPE.ELEMENT_COMPONENT
    type: {
      type: String,
      default: null,
    },
    // The type of this list e.g. DRAG_LIST.ELEMENTS
    listType: {
      type: String,
      default: null,
    },
    // What are the types of items that are allowed to drag on this list
    allowedTypes: {
      type: Array,
      default: null,
    },
    // A callback when an item is moved from this list
    moveAction: {
      type: Function,
      default: () => {},
    },
    // A callback when an item is removed from this list
    deleteAction: {
      type: Function,
      default: () => {},
    },
  },

  setup() {
    const { hasPermission: canEdit } = useProtect({
      permissions: [SYS_PERMISSIONS.SYS_PROJECTS_EDIT],
      roles: ['editor'],
    });

    return {
      canEdit,
    };
  },

  data() {
    return {
      dragEnter: false,
    };
  },

  computed: {
    ...mapGetters('application/drag', ['getItems']),
    ...mapGetters('project/data/components', ['getNodes']),

    /**
     * Returns an array with the actual component objects
     * @returns {Array}
     */
    comps() {
      const ids = this.components.map(c => c.component);
      return this.getNodes(ids);
    },

    /**
     * Return whether there is a component currently dragged over the component collection
     * @returns {Boolean}
     */
    isComponentDraggedOver() {
      const { type } = this.getItems;
      return (
        this.dragEnter &&
        (type === DRAG_TYPE.COMPONENT ||
          type === DRAG_TYPE.ATTRIBUTE_COMPONENT ||
          type === DRAG_TYPE.SEARCH_COMPONENT)
      );
    },

    classObject() {
      return {
        'component-drag-over': this.isComponentDraggedOver,
      };
    },
  },

  methods: {
    ...mapActions('application/drag', ['setTarget']),

    /**
     * Event handler for vddl drop event on a list. This is triggered before the move vddl event on the item itself.
     */
    onDropped(event) {
      console.log('ComponentCollection onDropped this.type is:', this.listType);
      this.setTarget({
        value: {
          type: this.listType,
          id: this.id,
          index: event.index,
        },
      });
    },

    /**
     * Event listener - dragenter event something is dragged inside the collection
     */
    // onDragEnter(event) {
    //   this.dragEnter = true;
    // },

    /**
     * Event listener - dragleave event something dragged is leaving the collection
     */
    // onDragLeave(event) {
    //   this.dragEnter = false;
    // },

    onMousedownCompgroup(event) {
      this.$emit('onMousedownCompgroup', event);
    },
  },
};
</script>
<style scoped lang="scss">
// .attribute .compgroup .vddl-dragover::before {
//   content: '';
//   position: absolute;
//   left: -2px;
//   top: -2px;
//   right: -2px;
//   bottom: -2px;
//   border: none;
//   background: #db841e;
//   border-radius: 8px;
//   // z-index: 1;
//   background-image: repeating-linear-gradient(
//     45deg,
//     transparent,
//     transparent 10px,
//     rgba(30, 33, 33, 1) 10px,
//     rgba(30, 33, 33, 1) 20px
//   );
//   background-size: 30px;
//   animation: spin 10s linear infinite;
// }

// @keyframes spin {
//   100% {
//     background-position: 500px 0;
//   }
// }

.attribute {
  .compgroup {
    // border: 2px dashed transparent;
    margin: 0;
    padding: 0;

    // margin: -3px 0 0 -6px;
    // border: 2px dashed #535f61;

    :deep(.v-popover) {
      height: 60px;
    }

    &.component-drag-over {
      &.is-empty {
        .vddl-list {
          border-color: #db841e;
          &:after {
            color: #db841e;
          }
        }
      }
    }
    &.is-empty {
      // position: relative;
      .vddl-list {
        margin: 0;
        border: 2px dashed #535f61;
        border-radius: 2px;
        display: flex;
        justify-content: center;
        align-items: center;
        min-height: 56px;
        .vddl-placeholder {
          display: none;
        }
        &:after {
          content: 'Drag components to add';
          text-align: center;
          text-transform: uppercase;
          color: #6c7b7d;
        }
      }
    }
    .vddl-list {
      border: 2px transparent;
      margin: -5px 0 0 -6px;
      padding: 0;
      display: flex;
      flex: 1;
      min-height: 66px;
      width: 100%;
      background: #2c3233;
      overflow: visible;
      .vddl-placeholder {
        pointer-events: none;
      }
    }
  }
}

.compgroup {
  margin: 0 20px 0 20px;
  padding: 16px 0 0 0;
  cursor: default;
  position: relative;
}
.compgroup .vddl-list {
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  margin-left: -4px;
  overflow: hidden;
  //min-height:20px;
}

.dragover {
  .compgroup .vddl-list {
    min-height: 70px;

    &.component-drag-over .placeholder {
      display: none;
    }

    &.component-drag-over * {
      pointer-events: none;
    }
  }

  .compgroup .placeholder {
    height: 64px;
    width: 64px;
    border-radius: 4px;
    overflow: hidden;
    margin: 3px 4px;
    position: relative;
    /* background:#aaa; */
    border: 2px dashed #db841e;
  }
}

/* .plank.vddl-dragover .compgroup .vddl-list, .plank .compgroup .vddl-list.vddl-dragover, .plank.vddl-dragover .compgroup .vddl-list.vddl-dragover{
    height:70px!important;

} */
</style>

<style>
.plank.component-drag-over .compgroup .droparea {
  display: block;
  visibility: visible;
}

.compgroup .vddl-placeholder {
  height: 64px;
  width: 64px;
  border-radius: 4px;
  overflow: hidden;
  margin: 3px 4px;
  position: relative;
  /* background:#aaa; */
  border: 2px dashed #db841e;
}

.compgroup .vddl-placeholder img {
  object-fit: cover;
  width: 100%;
  height: 100%;
  border-radius: 2px;
  opacity: 0.2;
}

.compgroup .vddl-draggable,
.compgroup .vddl-list {
  position: relative;
}

.compgroup .vddl-draggable {
  margin: 3px 4px;
}

.vddl-dragging * {
  cursor: move !important; /* fallback: no `url()` support or images disabled */
  cursor: -webkit-grabbing !important; /* Chrome 1-21, Safari 4+ */
  cursor: -moz-grabbing !important; /* Firefox 1.5-26 */
  cursor: grabbing !important; /* W3C standards syntax, should come least */
}

.compgroup .vddl-dragging-source {
  display: none;
}

/* .compgroup .vddl-placeholder .border-dance{

    background: -webkit-linear-gradient(0deg, #db841e 50%, transparent 50%), -webkit-linear-gradient(0deg, #db841e 50%, transparent 50%), -webkit-linear-gradient(90deg, #db841e 50%, transparent 50%), -webkit-linear-gradient(90deg, #db841e 50%, transparent 50%);
    background: -moz-linear-gradient(90deg, #db841e 50%, transparent 50%), -moz-linear-gradient(90deg, #db841e 50%, transparent 50%), -moz-linear-gradient(0deg, #db841e 50%, transparent 50%), -moz-linear-gradient(0deg, #db841e 50%, transparent 50%);
    background: linear-gradient(90deg, #db841e 50%, transparent 50%), linear-gradient(90deg, #db841e 50%, transparent 50%), linear-gradient(0deg, #db841e 50%, transparent 50%), linear-gradient(0deg, #db841e 50%, transparent 50%);
    background-repeat: repeat-x, repeat-x, repeat-y, repeat-y;
    background-size: 16px 4px, 16px 4px, 4px 16px, 4px 16px;
    -webkit-animation: border-dance 1.94s infinite linear;
    -moz-animation: border-dance 1.94s infinite linear;
    animation: border-dance 1.94s infinite linear;
}

@-webkit-keyframes border-dance{
    0%{background-position: -2px -2px, 58px 58px, -2px 62px, 62px -2px;}
    100%{background-position: 58px -2px, -2px 58px, -2px -2px, 62px 62px;}
}
@-moz-keyframes border-dance{
    0%{background-position: -2px -2px, 58px 58px, -2px 62px, 62px -2px;}
    100%{background-position: 58px -2px, -2px 58px, -2px -2px, 62px 62px;}
}
@keyframes border-dance{
    0%{background-position: -2px -2px, 58px 58px, -2px 62px, 62px -2px;}
    100%{background-position: 58px -2px, -2px 58px, -2px -2px, 62px 62px;}
} */

.compgroup .vddl-placeholder .droparea {
  height: 64px;
  width: 64px;
}
</style>
